import { Injectable } from '@angular/core';
import { AppState } from '@app/core/states/app.state';
import { RadioState } from '@app/core/states/radio.state';
import { BREVO_EVENT } from '@app/shared/modules/brevo-events';
import { Navigate } from '@ngxs/router-plugin';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { CloseCurrentPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, take } from 'rxjs/operators';
import { BrevoService } from '../../../../shared/common-services/src/lib/services/brevo.service';

import { Program, ProgramPreview } from '../models/programs.model';
import { ProgramsService } from '../services/programs.service';

import {
  CreateProgramFailure,
  CreateProgramRequest,
  CreateProgramSuccess,
  DeleteProgramFailure,
  DeleteProgramRequest,
  DeleteProgramSuccess,
  DuplicateProgramFailure,
  DuplicateProgramRequest,
  DuplicateProgramSuccess,
  EditProgramFailure,
  EditProgramRequest,
  EditProgramSuccess,
  EndEditProgram,
  PreviewProgramFailure,
  PreviewProgramRequest,
  PreviewProgramSuccess,
  ProgramAddPlaylistFailure,
  ProgramAddPlaylistRequest,
  ProgramAddPlaylistSuccess,
  ProgramDeletePlaylistFailure,
  ProgramDeletePlaylistRequest,
  ProgramDeletePlaylistSuccess,
  ProgramDuplicatePlaylistFailure,
  ProgramDuplicatePlaylistRequest,
  ProgramDuplicatePlaylistSuccess,
  ProgramEditPlaylistFailure,
  ProgramEditPlaylistRequest,
  ProgramEditPlaylistSuccess,
  ProgramMovePlaylistFailure,
  ProgramMovePlaylistRequest,
  ProgramMovePlaylistSuccess,
  ProgramPlaylistsFailure,
  ProgramPlaylistsRequest,
  ProgramPlaylistsSuccess,
  ProgramsFailure,
  ProgramsRequest,
  ProgramsSuccess,
  SetActionAfterEditProgram,
  StartEditProgram,
  StartProgramDetails,
  StopProgramDetails,
} from './programs.actions';

export class ProgramsStateModel {
  programs: Program[];
  editingProgram: Program;
  programDetails: Program;
  actionAfterEdit: any;
  programPreview: ProgramPreview[];
}

@State<ProgramsStateModel>({
  name: 'programs',
  defaults: {
    programs: null,
    editingProgram: null,
    programDetails: null,
    actionAfterEdit: null,
    programPreview: null,
  },
})
@Injectable()
export class ProgramsState {
  constructor(
    private readonly programsService: ProgramsService,
    private readonly store: Store,
    private readonly brevoService: BrevoService,
  ) {}

  @Selector()
  static programsLoaded(state: ProgramsStateModel): boolean {
    return !!state.programs;
  }

  @Selector()
  static programs(state: ProgramsStateModel): Program[] {
    return state.programs;
  }

  @Selector()
  static allProgramsForDropdown(state: ProgramsStateModel): SugestionInput[] {
    return state.programs.map(program => {
      return {
        id: program.id,
        name: program.name,
        color: program.color,
        extra: { duration: program.estimatedDuration },
      };
    });
  }

  @Selector()
  static allNotEmptyProgramsForDropdown(state: ProgramsStateModel): SugestionInput[] {
    return state.programs
      .filter(p => p.playlistsNumber > 0)
      .map(program => {
        return {
          id: program.id,
          name: program.name,
          color: program.color,
          extra: { duration: program.estimatedDuration },
        };
      });
  }

  @Selector()
  static hasPrograms(state: ProgramsStateModel): boolean {
    return !!state.programs.length;
  }

  @Selector()
  static hasNotEmptyPrograms(state: ProgramsStateModel): boolean {
    return !!state.programs.filter(p => p.playlistsNumber > 0).length;
  }

  @Selector()
  static editingProgram(state: ProgramsStateModel): Program {
    return state.editingProgram;
  }

  @Selector()
  static programDetails(state: ProgramsStateModel): Program {
    return state.programDetails;
  }

  @Selector()
  static programWithId(state: ProgramsStateModel): (programId: number) => Program {
    return programId =>
      state.programs?.find((program: Program) => program && program.id === programId);
  }

  @Action(ProgramsRequest)
  getPrograms(ctx: StateContext<ProgramsStateModel>) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.getPrograms(id)),
      mergeMap(programs => ctx.dispatch(new ProgramsSuccess(programs))),
      catchError(err => ctx.dispatch(new ProgramsFailure(err))),
    );
  }

  @Action(ProgramsSuccess)
  getProgramsSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { programs }: ProgramsSuccess,
  ) {
    ctx.patchState({
      programs,
    });
  }

  @Action(CreateProgramRequest)
  createProgram(
    ctx: StateContext<ProgramsStateModel>,
    { program }: CreateProgramRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return this.programsService.createProgram(id, program).pipe(
          map(data => {
            return { data, id };
          }),
        );
      }),
      mergeMap(({ data, id }) => ctx.dispatch(new CreateProgramSuccess(id, data))),
      catchError(err => ctx.dispatch(new CreateProgramFailure(err))),
    );
  }

  @Action(CreateProgramSuccess)
  createProgramSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { radioId, program }: CreateProgramSuccess,
  ) {
    if (!ctx.getState().programs.length) {
      this.brevoService.trackEvent(BREVO_EVENT.PROGRAM_CREATED);
    }
    ctx.setState(
      patch({
        programs: append([program]),
      }),
    );
    if (!this.store.selectSnapshot(AppState.onMobile)) {
      ctx.dispatch(
        new Navigate(['radio', radioId, 'scheduler', 'programs', 'program', program.id]),
      );
      ctx.dispatch(new CloseCurrentPanel(true));
    }
  }

  @Action(StartEditProgram)
  startEditProgram(ctx: StateContext<ProgramsStateModel>, { program }: StartEditProgram) {
    ctx.patchState({ editingProgram: program });
  }

  @Action(EndEditProgram)
  endEditProgram(ctx: StateContext<ProgramsStateModel>) {
    ctx.dispatch(new CloseCurrentPanel(true));
    ctx.patchState({ editingProgram: null });
  }

  @Action(ProgramPlaylistsRequest)
  getProgramPlaylists(
    ctx: StateContext<ProgramsStateModel>,
    { programId }: ProgramPlaylistsRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.getProgramPlaylists(id, programId)),
      mergeMap(data => ctx.dispatch(new ProgramPlaylistsSuccess(data))),
      catchError(err => ctx.dispatch(new ProgramPlaylistsFailure(err))),
    );
  }

  @Action(ProgramPlaylistsSuccess)
  getProgramPlaylistsSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { program }: ProgramPlaylistsSuccess,
  ) {
    ctx.setState(
      patch({
        programs: updateItem<Program>(p => p.id === program.id, patch({ ...program })),
      }),
    );
    if (program.id === ctx.getState().programDetails?.id) {
      ctx.patchState({ programDetails: program });
    }
  }

  @Action(EditProgramRequest)
  editProgram(ctx: StateContext<ProgramsStateModel>, { program }: EditProgramRequest) {
    let editingProgram = ctx.getState().editingProgram;
    const updateIntroOrExtro =
      editingProgram.idTrackIntro !== program.idTrackIntro ||
      editingProgram.idTrackExtro !== program.idTrackExtro;
    editingProgram = { ...editingProgram, ...program };

    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.editProgram(id, editingProgram)),
      mergeMap(() =>
        ctx.dispatch(new EditProgramSuccess(editingProgram, updateIntroOrExtro)),
      ),
      catchError(err => ctx.dispatch(new EditProgramFailure(err))),
    );
  }

  @Action(EditProgramSuccess)
  editProgramSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { program, updateIntroOrExtro }: EditProgramSuccess,
  ) {
    ctx.setState(
      patch({
        programs: updateItem<Program>(p => p.id === program.id, patch({ ...program })),
      }),
    );
    if (ctx.getState().actionAfterEdit) {
      return ctx.dispatch(new CloseCurrentPanel()).pipe(
        mergeMap(() => {
          const tmpAction = ctx.getState().actionAfterEdit;
          ctx.patchState({ actionAfterEdit: null });

          return ctx.dispatch(tmpAction);
        }),
      );
    } else if (!this.store.selectSnapshot(AppState.onMobile)) {
      ctx.dispatch(new CloseCurrentPanel(true));
    }
    // Fetch program to update estimated duration
    if (updateIntroOrExtro) {
      ctx.dispatch(new ProgramPlaylistsRequest(program.id));
    }
    ctx.patchState({
      programDetails: program,
    });

    return ctx.patchState({ editingProgram: null });
  }

  @Action(SetActionAfterEditProgram)
  setActionAfterEdit(
    ctx: StateContext<ProgramsStateModel>,
    { action }: SetActionAfterEditProgram,
  ) {
    ctx.patchState({ actionAfterEdit: action });
  }

  @Action(ProgramAddPlaylistRequest)
  programAddPlaylist(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlist, position }: ProgramAddPlaylistRequest,
  ) {
    if (!isNil(position)) {
      // FIXME Unoptimized workaround because api does not handle adding playlist other than at the end of the list
      return this.getCurrentRadioId().pipe(
        // FIXME 1 - Add playlist to the end of list
        mergeMap(id =>
          this.programsService
            .addPlaylistToProgram(id, programId, playlist)
            .pipe(mapTo(id)),
        ),
        // FIXME 2 - Retrieve all playlists because previous api call does not provide idProgramPlaylist
        mergeMap(id =>
          this.programsService.getProgramPlaylists(id, programId).pipe(
            map(data => ({
              id,
              // FIXME 2.1 - Retrieve last playlist, which should be the previously added one
              addedPlaylist: data.playlists[data.playlists.length - 1],
            })),
          ),
        ),
        // FIXME 3 - Finally! Move playlist to its specified position
        mergeMap(data =>
          this.programsService
            .editProgramPlaylist(data.id, programId, {
              ...data.addedPlaylist,
              position,
            })
            .pipe(
              map(id => ({
                id,
                playlist: {
                  ...data.addedPlaylist,
                  position,
                },
              })),
            ),
        ),
        mergeMap(data =>
          ctx.dispatch(new ProgramAddPlaylistSuccess(data.id, data.playlist)),
        ),
        catchError(err => ctx.dispatch(new ProgramAddPlaylistFailure(err))),
      );
    }

    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.addPlaylistToProgram(id, programId, playlist)),
      mergeMap(id => ctx.dispatch(new ProgramAddPlaylistSuccess(id))),
      catchError(err => ctx.dispatch(new ProgramAddPlaylistFailure(err))),
    );
  }

  @Action([
    ProgramAddPlaylistSuccess,
    ProgramEditPlaylistSuccess,
    ProgramDeletePlaylistSuccess,
    ProgramMovePlaylistSuccess,
    ProgramDuplicatePlaylistSuccess,
  ])
  programAddPlaylistSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { programId }: ProgramAddPlaylistSuccess,
  ) {
    return ctx.dispatch(new ProgramPlaylistsRequest(programId));
  }

  @Action(ProgramEditPlaylistRequest)
  programEditPlaylist(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlist }: ProgramEditPlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.editProgramPlaylist(id, programId, playlist)),
      mergeMap(id => ctx.dispatch(new ProgramEditPlaylistSuccess(id))),
      catchError(err => ctx.dispatch(new ProgramEditPlaylistFailure(err))),
    );
  }

  @Action(ProgramMovePlaylistRequest)
  programMovePlaylist(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlist }: ProgramEditPlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.editProgramPlaylist(id, programId, playlist)),
      mergeMap(id => ctx.dispatch(new ProgramMovePlaylistSuccess(id))),
      catchError(err => ctx.dispatch(new ProgramMovePlaylistFailure(err))),
    );
  }

  @Action(ProgramDuplicatePlaylistRequest)
  programDuplicatePlaylist(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlist, position }: ProgramDuplicatePlaylistRequest,
  ) {
    // FIXME Unoptimized workaround because api does not handle playlist duplication correctly
    return this.getCurrentRadioId().pipe(
      // FIXME 1 - Add playlist to the end of list
      mergeMap(id =>
        this.programsService
          .addPlaylistToProgram(id, programId, playlist)
          .pipe(mapTo(id)),
      ),
      // FIXME 2 - Retrieve all playlists because previous api call does not provide idProgramPlaylist
      mergeMap(id =>
        this.programsService.getProgramPlaylists(id, programId).pipe(
          map(data => ({
            id,
            // FIXME 2.1 - Retrieve last playlist, which should be the previously added one
            addedPlaylist: data.playlists[data.playlists.length - 1],
          })),
        ),
      ),
      // FIXME 3 - Finally! Move playlist to its specified position
      mergeMap(data =>
        this.programsService
          .editProgramPlaylist(data.id, programId, {
            ...data.addedPlaylist,
            position,
          })
          .pipe(
            map(id => ({
              id,
              playlist: {
                ...data.addedPlaylist,
                position,
              },
            })),
          ),
      ),
      mergeMap(data =>
        ctx.dispatch(new ProgramDuplicatePlaylistSuccess(data.id, data.playlist)),
      ),
      catchError(err => ctx.dispatch(new ProgramDuplicatePlaylistFailure(err))),
    );
  }

  @Action(DeleteProgramRequest)
  deleteProgram(
    ctx: StateContext<ProgramsStateModel>,
    { programId }: DeleteProgramRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.deleteProgram(id, programId).pipe(mapTo(id))),
      mergeMap(id => ctx.dispatch(new DeleteProgramSuccess(id, programId))),
      catchError(err => ctx.dispatch(new DeleteProgramFailure(err))),
    );
  }

  @Action(DeleteProgramSuccess)
  deleteProgramSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { radioId, programId }: DeleteProgramSuccess,
  ) {
    ctx.dispatch(new Navigate(['radio', radioId, 'scheduler', 'programs']));
    ctx.setState(
      patch({
        programs: removeItem((p: Program) => p.id === programId),
      }),
    );
  }

  @Action(DuplicateProgramRequest)
  duplicateProgram(
    ctx: StateContext<ProgramsStateModel>,
    { program }: DuplicateProgramRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.programsService.duplicateProgram(id, program).pipe(
          map(newProgramId => {
            const newProgram = { ...program, id: newProgramId };

            return { id, newProgram };
          }),
        ),
      ),
      mergeMap(({ id, newProgram }) =>
        ctx.dispatch(new DuplicateProgramSuccess(id, newProgram)),
      ),
      catchError(err => ctx.dispatch(new DuplicateProgramFailure(err))),
    );
  }

  @Action(DuplicateProgramSuccess)
  duplicateProgramSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { radioId, program }: DuplicateProgramSuccess,
  ) {
    ctx.setState(
      patch({
        programs: append([program]),
      }),
    );
    ctx.dispatch(
      new Navigate(['radio', radioId, 'scheduler', 'programs', 'program', program.id]),
    );
  }

  @Action(ProgramDeletePlaylistRequest)
  deletePlaylist(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlistId }: ProgramDeletePlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.deleteProgramPlaylist(id, playlistId)),
      mergeMap(() =>
        ctx.dispatch(new ProgramDeletePlaylistSuccess(programId, playlistId)),
      ),
      catchError(err => ctx.dispatch(new ProgramDeletePlaylistFailure(err))),
    );
  }

  @Action(ProgramDeletePlaylistSuccess)
  deletePlaylistSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { programId, playlistId }: ProgramDeletePlaylistSuccess,
  ) {
    const program = ctx.getState().programs.find(p => p.id === programId);
    program.playlists = program.playlists.filter(
      pl => pl.idProgramPlaylist !== playlistId,
    );
    ctx.setState(
      patch({
        programs: updateItem<Program>(p => p.id === program.id, patch({ ...program })),
      }),
    );
  }

  @Action(PreviewProgramRequest)
  previewProgram(
    ctx: StateContext<ProgramsStateModel>,
    { programId, cycleIdx }: PreviewProgramRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.programsService.previewProgram(id, programId, cycleIdx)),
      mergeMap(programPreview => ctx.dispatch(new PreviewProgramSuccess(programPreview))),
      catchError(err => ctx.dispatch(new PreviewProgramFailure(err))),
    );
  }

  @Action(PreviewProgramSuccess)
  previewProgramSuccess(
    ctx: StateContext<ProgramsStateModel>,
    { programPreview }: PreviewProgramSuccess,
  ) {
    ctx.patchState({
      programPreview,
    });
  }

  @Action(StartProgramDetails)
  startProgramDetails(
    ctx: StateContext<ProgramsStateModel>,
    { program }: StartProgramDetails,
  ) {
    ctx.patchState({
      programDetails: program,
    });
  }

  @Action(StopProgramDetails)
  stopProgramDetails(ctx: StateContext<ProgramsStateModel>) {
    ctx.dispatch(new CloseCurrentPanel(true));
    ctx.patchState({
      programDetails: null,
    });
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
